import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ComponentsModule } from '../../../components/components.module';
import { TwoFactorService } from '../../../core/services/two-factor.service';
import { ImpersonationService } from '../../../core/services/impersonation.service';
import { RememberMeStorageService } from '../../../core/services/remember-me-storage.service';
import { UserService } from '../../../core/services/user.service';
import { ConfirmationService } from '../../../core/services/confirmation.service';
import { TwoFactorMethod } from '../../../core/interfaces/two-factor';
import { mapTwoFactorError } from '../../../core/utils/two-factor-error.utils';
import { TwoFactorAddModalComponent } from './two-factor-add-modal/two-factor-add-modal.component';
import { RecoveryCodesListComponent } from './recovery-codes-list/recovery-codes-list.component';

/** Whether the recovery-codes modal shows the current set or generates a new one */
export type RecoveryCodesAction = 'show' | 'regenerate';

const LOGIN_ROUTE = '/account/login';

/**
 * The two-factor section of the profile page (HD-2013): status badge, list
 * of authenticators, add / remove / disable, and the recovery codes.
 *
 * Trusted devices stay in the profile component; it listens to
 * (statusChange) and reloads them after any change made here.
 *
 * While a super-admin impersonates the user the list is read-only: the
 * backend refuses every 2FA write for an impersonation token.
 *
 * Every modal here has two closing paths:
 * - close*Modal(): what the backdrop, the X, Escape and Cancel call. It
 *   refuses while that modal's request is in flight.
 * - reset*Modal(): the unconditional reset used after a request finished.
 */
@Component({
  selector: 'app-two-factor-settings',
  templateUrl: './two-factor-settings.component.html',
  styleUrl: './two-factor-settings.component.scss',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ComponentsModule, TwoFactorAddModalComponent, RecoveryCodesListComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TwoFactorSettingsComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  /** Seed from the cached user (two_factor_confirmed_at) until 2fa/status answers */
  @Input() initiallyEnabled = false;

  /** Emits the enabled state after the status loads and after every change */
  @Output() statusChange = new EventEmitter<boolean>();

  isEnabled = false;
  isImpersonating = false;
  methods: TwoFactorMethod[] = [];
  isLoadingMethods = false;
  loadError = '';
  /** Shown after the first authenticator: the next one needs a session that logged in with a code */
  showReloginHint = false;

  // Add
  showAddModal = false;

  // Remove one authenticator
  methodToRemove: TwoFactorMethod | null = null;
  removePassword = '';
  removeError = '';
  isRemoving = false;

  // Disable 2FA
  showDisableModal = false;
  disablePassword = '';
  disableError = '';
  isDisabling = false;

  // Recovery codes
  showRecoveryCodesModal = false;
  recoveryCodesAction: RecoveryCodesAction = 'show';
  recoveryPassword = '';
  recoveryError = '';
  isLoadingRecoveryCodes = false;
  recoveryCodes: string[] = [];
  /** True once the backend answered, so an empty list means "none left" rather than "not asked yet" */
  recoveryCodesLoaded = false;

  // Session never passed 2FA: the user has to log in again with a code
  showSessionRequiredModal = false;
  isLoggingOut = false;

  constructor(
    private twoFactorService: TwoFactorService,
    private impersonationService: ImpersonationService,
    private rememberMeStorage: RememberMeStorageService,
    private userService: UserService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.isEnabled = this.initiallyEnabled;
    this.isImpersonating = this.impersonationService.isImpersonating();
    this.loadStatus();
  }

  /** Write actions are hidden while impersonating; the backend refuses them anyway. */
  get canManage(): boolean {
    return !this.isImpersonating;
  }

  get isLastAuthenticator(): boolean {
    return this.methods.length <= 1;
  }

  /** The password was accepted but the account has used up every recovery code. */
  get hasNoRecoveryCodesLeft(): boolean {
    return this.recoveryCodesLoaded && this.recoveryCodes.length === 0;
  }

  /**
   * Escape closes the modal that is open, through the same guarded path as
   * the backdrop and the X. The add modal listens for Escape itself.
   */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.methodToRemove) {
      this.closeRemoveModal();
    } else if (this.showDisableModal) {
      this.closeDisableModal();
    } else if (this.showRecoveryCodesModal) {
      this.closeRecoveryCodesModal();
    } else if (this.showSessionRequiredModal) {
      this.closeSessionRequiredModal();
    }
  }

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  loadStatus(): void {
    this.twoFactorService.getStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.setEnabled(!!response.data?.enabled);
          if (this.isEnabled) {
            this.loadMethods();
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadError = this.translate.instant('profile.authenticators.errors.load_failed');
          this.cdr.markForCheck();
        },
      });
  }

  loadMethods(): void {
    this.isLoadingMethods = true;
    this.loadError = '';

    this.twoFactorService.getMethods()
      .pipe(
        finalize(() => {
          this.isLoadingMethods = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          this.methods = response.data?.methods ?? [];
          this.cdr.markForCheck();
        },
        error: () => {
          this.methods = [];
          this.loadError = this.translate.instant('profile.authenticators.errors.load_failed');
          this.cdr.markForCheck();
        },
      });
  }

  private setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    if (!enabled) {
      this.methods = [];
      // The hint only makes sense while 2FA is on
      this.showReloginHint = false;
    }
    this.statusChange.emit(enabled);
  }

  // ---------------------------------------------------------------------------
  // Add
  // ---------------------------------------------------------------------------

  openAddModal(): void {
    this.showAddModal = true;
  }

  closeAddModal(): void {
    this.showAddModal = false;
  }

  onAuthenticatorAdded(switchedTwoFactorOn: boolean): void {
    this.showAddModal = false;

    if (switchedTwoFactorOn) {
      // The backend revoked every trusted device, this browser included.
      this.rememberMeStorage.clearRememberToken();
      this.showReloginHint = true;
      this.confirmationService.swalSuccess(this.translate.instant('profile.2fa_enabled_success'));
    } else {
      this.confirmationService.swalSuccess(this.translate.instant('profile.authenticators.added_success'));
    }

    this.loadStatus();
  }

  // ---------------------------------------------------------------------------
  // Remove one authenticator
  // ---------------------------------------------------------------------------

  openRemoveModal(method: TwoFactorMethod): void {
    this.methodToRemove = method;
    this.removePassword = '';
    this.removeError = '';
  }

  /** User-initiated close: refused while the remove request is running. */
  closeRemoveModal(): void {
    if (this.isRemoving) {
      return;
    }
    this.resetRemoveModal();
  }

  private resetRemoveModal(): void {
    this.methodToRemove = null;
    this.removePassword = '';
    this.removeError = '';
  }

  confirmRemove(): void {
    if (!this.methodToRemove || this.isRemoving) {
      return;
    }
    if (!this.removePassword) {
      this.removeError = this.translate.instant('profile.password_required');
      return;
    }

    this.isRemoving = true;
    this.removeError = '';

    this.twoFactorService.removeMethod(this.methodToRemove.id, this.removePassword)
      .pipe(
        finalize(() => {
          this.isRemoving = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => this.onAuthenticatorRemoved(!!response.data?.enabled),
        error: (error: unknown) => this.onRemoveFailed(error),
      });
  }

  private onAuthenticatorRemoved(stillEnabled: boolean): void {
    // Removing an authenticator always revokes every trusted device.
    this.rememberMeStorage.clearRememberToken();
    this.resetRemoveModal();

    const successKey = stillEnabled
      ? 'profile.authenticators.removed_success'
      : 'profile.authenticators.removed_last_success';
    this.confirmationService.swalSuccess(this.translate.instant(successKey));

    this.setEnabled(stillEnabled);
    if (stillEnabled) {
      this.loadMethods();
    }
    this.cdr.markForCheck();
  }

  private onRemoveFailed(error: unknown): void {
    const message = this.messageFor(error, 'profile.authenticators.errors.remove_failed');
    const mapped = mapTwoFactorError(error, '');

    if (mapped.kind === 'method_not_found') {
      // Someone else already removed it: the modal has nothing left to act on.
      this.resetRemoveModal();
      this.confirmationService.swalError(message);
      this.loadMethods();
    } else {
      this.removeError = message;
    }
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------------
  // Disable 2FA
  // ---------------------------------------------------------------------------

  openDisableModal(): void {
    this.showDisableModal = true;
    this.disablePassword = '';
    this.disableError = '';
  }

  /** User-initiated close: refused while the disable request is running. */
  closeDisableModal(): void {
    if (this.isDisabling) {
      return;
    }
    this.resetDisableModal();
  }

  private resetDisableModal(): void {
    this.showDisableModal = false;
    this.disablePassword = '';
    this.disableError = '';
  }

  confirmDisable(): void {
    if (this.isDisabling) {
      return;
    }
    if (!this.disablePassword) {
      this.disableError = this.translate.instant('profile.password_required');
      return;
    }

    this.isDisabling = true;
    this.disableError = '';

    this.twoFactorService.disable(this.disablePassword)
      .pipe(
        finalize(() => {
          this.isDisabling = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.rememberMeStorage.clearRememberToken();
          this.resetDisableModal();
          this.confirmationService.swalSuccess(this.translate.instant('profile.2fa_disabled_success'));
          this.setEnabled(false);
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.disableError = this.messageFor(error, 'profile.2fa_disable_failed');
          this.cdr.markForCheck();
        },
      });
  }

  // ---------------------------------------------------------------------------
  // Recovery codes: both actions ask for the password before any request
  // ---------------------------------------------------------------------------

  openRecoveryCodesModal(action: RecoveryCodesAction): void {
    this.showRecoveryCodesModal = true;
    this.recoveryCodesAction = action;
    this.recoveryPassword = '';
    this.recoveryError = '';
    this.recoveryCodes = [];
    this.recoveryCodesLoaded = false;
  }

  /** User-initiated close: refused while the codes are being requested. */
  closeRecoveryCodesModal(): void {
    if (this.isLoadingRecoveryCodes) {
      return;
    }
    this.resetRecoveryCodesModal();
  }

  private resetRecoveryCodesModal(): void {
    this.showRecoveryCodesModal = false;
    this.recoveryPassword = '';
    this.recoveryError = '';
    this.recoveryCodes = [];
    this.recoveryCodesLoaded = false;
  }

  /** From the "no codes left" notice straight into the regenerate flow, same modal. */
  switchToRegenerate(): void {
    this.openRecoveryCodesModal('regenerate');
  }

  submitRecoveryPassword(): void {
    if (this.isLoadingRecoveryCodes) {
      return;
    }
    if (!this.recoveryPassword) {
      this.recoveryError = this.translate.instant('profile.password_required');
      return;
    }

    this.isLoadingRecoveryCodes = true;
    this.recoveryError = '';

    const request = this.recoveryCodesAction === 'regenerate'
      ? this.twoFactorService.regenerateRecoveryCodes(this.recoveryPassword)
      : this.twoFactorService.getRecoveryCodes(this.recoveryPassword);

    request
      .pipe(
        finalize(() => {
          this.isLoadingRecoveryCodes = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => this.onRecoveryCodesLoaded(response.data?.recovery_codes ?? []),
        error: (error: unknown) => {
          this.recoveryError = this.messageFor(error, 'profile.authenticators.errors.recovery_codes_failed');
          this.cdr.markForCheck();
        },
      });
  }

  private onRecoveryCodesLoaded(codes: string[]): void {
    this.recoveryPassword = '';

    // Regenerate always creates a fresh set; an empty answer there is a failure, not "none left".
    if (codes.length === 0 && this.recoveryCodesAction === 'regenerate') {
      this.recoveryError = this.translate.instant('profile.authenticators.errors.recovery_codes_failed');
      this.cdr.markForCheck();
      return;
    }

    this.recoveryCodes = codes;
    this.recoveryCodesLoaded = true;
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------------
  // Session that never passed 2FA
  // ---------------------------------------------------------------------------

  onSessionRequired(): void {
    this.closeAllModals();
    this.showSessionRequiredModal = true;
    this.cdr.markForCheck();
  }

  /** User-initiated close: refused while the logout request is running. */
  closeSessionRequiredModal(): void {
    if (this.isLoggingOut) {
      return;
    }
    this.showSessionRequiredModal = false;
  }

  /** Same logout path as the topbar: revoke the token, then back to login. */
  logInAgain(): void {
    if (this.isLoggingOut) {
      return;
    }
    this.isLoggingOut = true;
    this.userService.logout()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.router.navigate([LOGIN_ROUTE]),
        error: () => this.router.navigate([LOGIN_ROUTE]),
      });
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Translates a failed request. A "session required" refusal is not shown
   * inline: it opens the re-login prompt instead and returns an empty string.
   */
  private messageFor(error: unknown, fallbackKey: string): string {
    const mapped = mapTwoFactorError(error, fallbackKey);
    if (mapped.kind === 'session_required') {
      this.onSessionRequired();
      return '';
    }
    return this.translate.instant(mapped.translationKey);
  }

  /** Unconditional: a request just ended with a refusal, so nothing is in flight any more. */
  private closeAllModals(): void {
    this.showAddModal = false;
    this.resetRemoveModal();
    this.resetDisableModal();
    this.resetRecoveryCodesModal();
  }
}
